import { useEffect, useRef, useState } from "react";
import { croppedStyle, croppedUrlStyle } from "./tileset.js";
import { overrideFor, bboxOf } from "./overrides.js";

// ─────────────────────────────────────────────────────────────
// VIGNETTE D'UN OBJET — un seul composant pour la boutique, le sac,
// l'éditeur, les confréries et le panneau admin.
//
// POURQUOI CE FICHIER EXISTE
// Une image remplacée par l'admin s'affichait « trop basse » et plus
// petite que les autres : elle était rendue ENTIÈRE (gabarit 256×352)
// en object-fit contain, donc un sujet dessiné en bas de son gabarit
// occupait un tiers de la vignette, collé au bord inférieur — alors
// qu'une tuile Kenney, elle, était recadrée sur sa boîte englobante.
// Deux cadrages différents pour la même liste d'objets.
//
// Ici, un seul chemin : on mesure la boîte englobante réelle de
// l'image (canal alpha, mesurée une fois à l'upload puis mémorisée)
// et on applique EXACTEMENT la formule de croppedStyle(). Même point
// de référence, même taille apparente, quelle que soit la hauteur de
// calage choisie avec le losange dans l'éditeur — le calage règle la
// position sur l'île, il n'a plus aucun effet sur les vignettes.
// ─────────────────────────────────────────────────────────────
export default function Sprite({ item, size = 56, className = "" }) {
  const ref = useRef(null);
  const [, force] = useState(0);

  useEffect(() => {
    if (item?.kind !== "proc" || !ref.current) return;
    const c = ref.current;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    c.width = size * dpr;
    c.height = size * dpr;
    const ctx = c.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, size, size);
    item.draw(ctx, size / 2, size * 0.94, size / 78, 0, 1);
  }, [item, size]);

  if (!item) return <div style={{ width: size, height: size }} className={className} />;

  if (item.kind === "proc") {
    return (
      <canvas ref={ref} className={className}
        style={{ width: size, height: size }} aria-label={item.name} />
    );
  }

  const [w, d] = item.size || [1, 1];
  const ov = overrideFor(item.id);
  // bboxOf renvoie null la toute première fois (mesure asynchrone) :
  // on redessine dès qu'elle est connue plutôt que d'afficher un
  // cadrage provisoire faux.
  const bbox = ov ? bboxOf(item.id, () => force((n) => n + 1)) : null;

  const style = ov
    ? croppedUrlStyle(ov.url, bbox, size)
    : item.tile
      ? croppedStyle(item.tile, size)
      : { width: size, height: size };

  return (
    <div className={`relative ${className}`} style={{ width: size, height: size }}>
      <div style={style} role="img" aria-label={item.name} />
      {(w > 1 || d > 1) && (
        <span className="absolute -bottom-0.5 -right-0.5 text-[9px] font-bold bg-foreground text-background rounded px-1 leading-tight">
          {w}×{d}
        </span>
      )}
    </div>
  );
}
